/* ============================================================================
 * Content for the product landing page.
 *
 * One fictional candidate (Ananya Sharma) and one role (Senior React Engineer)
 * run through every section so the page reads as a single session. Values are
 * sample data; every UI element they populate mirrors a real screen. Before
 * adding a claim here, confirm the feature exists in the codebase — see the
 * "claims must match the product" note in the team docs.
 * ========================================================================== */
/** VA-29222 / VA-29223: the product's own About and Contact pages. */
export const ABOUT_PATH = '/ai-interview/about';
export const CONTACT_PATH = '/ai-interview/contact';
/** Published for AI Interview questions in public/integration-guide.html. */
export const CONTACT_EMAIL = 'interviews@vriti.ai';
/** The Vriti product family, linked from the footer. */
export const VRITI_PRODUCTS = [
    { l: 'AI Interview', href: 'https://vriti.ai/products/ai-interview' },
    { l: 'ATS', href: 'https://vriti.ai/products/ats' },
    { l: 'Scheduler', href: 'https://vriti.ai/products/scheduler' },
    { l: 'Mailbox', href: 'https://vriti.ai/products/mailbox' },
    { l: 'Vendor Management', href: 'https://vriti.ai/products/vendor-management' },
    { l: 'Marketplace', href: 'https://vriti.ai/products/marketplace' },
    { l: 'All products', href: 'https://vriti.ai/products' },
];
export const CANDIDATE = {
    name: 'Ananya Sharma',
    initials: 'AS',
    email: 'ananya.sharma@mail.com',
    experience: '6 yrs',
    location: 'Bengaluru',
};
export const ROLE = 'Senior React Engineer';
export const BRIEF = 'Senior React Engineer, 5+ years';
export const CRITERIA = [
    {
        id: 'tech',
        label: 'Technical Skills',
        weight: 40,
        score: 91,
        indicator: 'Performance tuning · pass mark 60%',
    },
    {
        id: 'ps',
        label: 'Problem Solving',
        weight: 30,
        score: 84,
        indicator: 'Isolates the bottleneck · min 65%',
    },
    {
        id: 'comm',
        label: 'Communication',
        weight: 20,
        score: 88,
        indicator: 'Structured explanation · min 55%',
    },
    {
        id: 'role',
        label: 'Role Knowledge',
        weight: 10,
        score: 83,
        indicator: 'Frontend architecture · min 50%',
    },
];
/** Weighted overall from CRITERIA. 87.5 → Shortlisted — strong (pass ≥ 85). */
export const OVERALL = CRITERIA.reduce((s, c) => s + (c.score * c.weight) / 100, 0);
export function band(score) {
    if (score >= 80)
        return { label: 'Strong', tone: 'pos' };
    if (score >= 60)
        return { label: 'Moderate', tone: 'warn' };
    return { label: 'Needs work', tone: 'neg' };
}
/** Screening-stage recommendation labels, as the report prints them. */
export const RECOMMENDATIONS = [
    { label: 'Shortlisted — strong', rule: 'Pass ≥ 85', tone: 'pos' },
    { label: 'Shortlisted', rule: 'Above cut-off', tone: 'pos' },
    { label: 'Rejected — borderline', rule: 'Fail ≥ 45', tone: 'warn' },
    { label: 'Rejected', rule: 'Below 45', tone: 'neg' },
];
/* ------------------------------------------------------- hero live session */
export const OBJECTIVES = [
    { n: '1', title: 'Frontend architecture', state: 'done', coverage: 100 },
    { n: '2', title: 'React performance', state: 'live', coverage: 64 },
    { n: '3', title: 'Testing & quality', state: 'next', coverage: 0 },
    { n: '4', title: 'Collaboration', state: 'next', coverage: 0 },
];
export const SESSION = {
    question: 'Your trading dashboard dropped frames under load. What did you change first?',
    answer: 'I separated event delivery from UI rendering. The socket writes into a normalized store outside React, and each panel subscribes to its own slice through a selector — so a burst of two hundred messages a second no longer re-renders the whole feed.',
    evidence: 'separated event delivery from UI rendering',
    gap: 'No measurement given. How was the improvement verified?',
    followUp: 'How did you confirm it worked — what did you measure before and after the change?',
};
/** Minutes on a 45-minute session. */
export const TIMELINE = {
    length: 45,
    now: 31 + 21 / 60,
    segments: [
        { from: 0, to: 3, label: 'Intro' },
        { from: 3, to: 18, label: 'Frontend architecture' },
        { from: 18, to: 34, label: 'React performance' },
        { from: 34, to: 41, label: 'Testing & quality' },
        { from: 41, to: 45, label: 'Collaboration' },
    ],
    evidence: [5.2, 8.7, 13.1, 16.4, 21.5, 26.2],
    followUps: [9.3, 24.7],
    integrity: [{ at: 12.7, label: 'Tab switch · 2s' }],
};
/* ------------------------------------------------------------- problem */
export const PROBLEMS = [
    {
        t: 'Applications keep piling up',
        d: 'More profiles to review before the first conversation.',
    },
    {
        t: 'Scheduling eats into the day',
        d: 'Busy calendars can delay the next round.',
    },
    {
        t: 'Every panel sets a different bar',
        d: 'Different questions make candidate results harder to compare.',
    },
    {
        t: 'The context gets lost',
        d: 'Notes can miss the detail behind a verdict.',
    },
];
export const DEBRIEF = [
    {
        who: 'Interviewer A',
        when: 'Mon 11:40',
        rating: 'Strong yes',
        note: 'Really sharp. Knows React inside out.',
        flag: 'No evidence cited',
    },
    {
        who: 'Interviewer B',
        when: 'Wed 18:05',
        rating: 'Lean no',
        note: 'Seemed nervous on the system design part?',
        flag: 'Written 2 days later',
    },
    {
        who: 'Interviewer C',
        when: 'Tue 09:15',
        rating: '3 / 5',
        note: 'Asked about testing instead — fine.',
        flag: 'Different question set',
    },
];
/* ------------------------------------------------------------- hero H1s */
/**
 * One hero heading per Google Ads ad group, keyed by the `utm_term` slug the
 * ads carry. `hl` is the highlighted tail. Unknown or missing → the default.
 */
export const HERO_TITLES = {
    'ai-interview-software': {
        lead: 'Run AI Interviews That Score Every Candidate ',
        hl: 'on Evidence',
    },
    'ai-interviewer': {
        lead: 'Your AI Interviewer: Structured Interviews, ',
        hl: 'Scored on Evidence',
    },
    'ai-screening': {
        lead: 'AI Screening Interviews for Every Candidate, ',
        hl: 'Without the Scheduling',
    },
    'ai-voice-interview': {
        lead: 'AI Voice Interviews That Turn Every Answer ',
        hl: 'Into Evidence',
    },
    'ai-technical-interview': {
        lead: 'AI Technical Interviews With Live Coding Rounds, ',
        hl: 'Scored Automatically',
    },
    'automated-interview': {
        lead: 'Automate First-Round Interviews. ',
        hl: 'Keep Every Hiring Decision Yours.',
    },
};
export const DEFAULT_HERO_TITLE = 'ai-interview-software';
/* --------------------------------------------------------- business value */
export const VALUE = [
    {
        t: 'Less chasing',
        d: 'No more “did you get my invite?” Every invitation and every candidate’s progress sits on one dashboard.',
    },
    {
        t: 'Clearer reviews',
        d: 'Each AI screening interview ends in one scored report. No scattered notes to piece together.',
    },
    {
        t: 'A stronger handoff',
        d: 'Your hiring manager sees the same transcript evidence you do.',
    },
];
/* ------------------------------------------------- five-stage assessment */
// Checklists are limited to what ships: no bulk invites, reminders or
// knockout criteria — see the "claims must match the product" note.
export const STAGES = [
    {
        key: 'standard',
        label: 'Set the Interview Standard',
        title: 'Define what the role actually requires — once.',
        items: [
            'Role and experience requirements',
            'Skills and competencies',
            'Interview questions and qualifying criteria',
            'Evaluation categories, scoring and weightage',
            'Technical or coding requirements',
        ],
        note: 'Same role. Same standard. Clearer comparison.',
    },
    {
        key: 'invite',
        label: 'Invite Candidates',
        title: 'Send interview invitations without writing a single email.',
        items: [
            'One interview link per candidate',
            'Scheduled interview slots',
            'Candidate status tracking',
        ],
        note: 'See who’s scheduled, who’s in progress, and who’s done — at a glance.',
    },
    {
        key: 'conduct',
        label: 'Vriti Conducts the Interview',
        title: 'Candidates interview on their own time, not your recruiter’s.',
        items: [
            'AI-led question and answer rounds',
            'Role-based and follow-up questions',
            'Technical evaluation',
            'Coding rounds, where assigned',
            'Communication and behavioural assessment',
        ],
        note: 'Every response is captured — nothing depends on someone’s notes.',
    },
    {
        key: 'evaluate',
        label: 'AI Prepares the Evaluation',
        title: 'The interview becomes something your team can actually use.',
        items: [
            'Transcript and candidate summary',
            'Skill-wise evaluation',
            'Strengths and gaps',
            'Role-based scorecard',
            'Screening recommendation',
        ],
        note: 'No reconstructing the conversation from memory or scattered notes.',
    },
    {
        key: 'decide',
        label: 'Compare and Decide',
        title: 'Every candidate for the role, measured the same way.',
        items: [
            'Overall score',
            'Core skills',
            'Technical / task performance',
            'Communication',
            'Problem solving and behavioural fit',
        ],
        note: 'Shortlist, hold or reject — the decision stays yours.',
    },
];
/* ------------------------------------------------------ interview engines */
export const ENGINES = [
    {
        t: 'Real-time voice',
        lead: 'Hiring a senior engineer, a specialist or a leader?',
        d: 'Interview them the way you would yourself: a spoken, turn-by-turn conversation with an evidence-backed report at the end.',
    },
    {
        t: 'Volume format',
        lead: 'Screening hundreds for one opening?',
        d: 'Run every candidate through the same structured interview in a cost-optimised format, then focus your time on the strongest.',
    },
];
/* --------------------------------------------------------------- audience */
export const AUDIENCE = [
    {
        t: 'For recruiters',
        d: 'Each candidate gets their own link, so many interviews can run at the same time.',
    },
    {
        t: 'For candidates',
        d: 'The AI interviewer asks a follow-up when an answer is incomplete.',
    },
    {
        t: 'For hiring managers',
        d: 'Each report shows six live proctoring signals as one integrity score out of 100.',
    },
];
/* --------------------------------------------------------- role relevance */
export const ROLES = [
    { t: 'Technical roles', d: 'Add coding rounds in any of 11 languages.' },
    { t: 'Specialist roles', d: 'Assess role-specific knowledge and judgment.' },
    { t: 'Graduate hiring', d: 'Apply clear criteria to early-career candidates.' },
    {
        t: 'High-volume hiring',
        d: 'Use a repeatable process across larger candidate groups.',
    },
];
/* -------------------------------------------------------------- integrity */
export const INTEGRITY_SIGNALS = [
    'Multiple faces',
    'Left frame',
    'Tab switches',
    'Camera stops',
    'Eye movements',
    'Head movements',
];
export const INTEGRITY_VERDICTS = [
    { label: 'No integrity concerns', tone: 'pos' },
    { label: 'Review recommended', tone: 'warn' },
    { label: 'Integrity concern', tone: 'neg' },
];
/* -------------------------------------------------------- conversation */
export const CONVERSATION_STEPS = [
    {
        k: 'Ask',
        title: 'The AI asks.',
        body: 'One question at a time, drawn from the objective it’s covering. It speaks, then listens — no forms, no multiple choice.',
    },
    {
        k: 'Answer',
        title: 'The candidate answers out loud.',
        body: 'The transcript streams in as they speak. Pauses and restarts are fine; the model waits for a complete thought.',
    },
    {
        k: 'Check',
        title: 'The answer is checked against the bar.',
        body: 'Each objective lists what a complete answer contains. Here: an approach, the scale, and how it was verified.',
    },
    {
        k: 'Follow up',
        title: 'Missing evidence becomes the next question.',
        body: 'The candidate never measured the result, so the AI asks about it — before moving on, not in the debrief.',
    },
];
export const EVIDENCE_CHECKS = [
    { label: 'Approach described', got: 'Decoupled store, per-panel selectors', step: 2 },
    { label: 'Scale quantified', got: '~200 messages / second', step: 2 },
    { label: 'Result verified', got: null, step: 2 },
];
/* ------------------------------------------------------------ evaluation */
export const CHAIN = [
    {
        c: 'tech',
        q: 'Dashboard dropped frames under load — what changed first?',
        r: '…I separated event delivery from UI rendering. The socket writes into a normalized store outside React…',
        e: 'Separated event delivery from UI rendering',
        s: 91,
        t: '31:04',
    },
    {
        c: 'ps',
        q: 'How did you confirm the fix?',
        r: '…recorded a profile at 200 msg/s, then compared commit counts per second before and after…',
        e: 'Measured commits/sec with the profiler before and after',
        s: 84,
        t: '32:10',
    },
    {
        c: 'comm',
        q: 'Explain the trade-off to a product manager.',
        r: '…the feed can lag the socket by one frame, and in exchange the page stays usable during spikes…',
        e: 'Framed latency vs. responsiveness in user terms',
        s: 88,
        t: '33:02',
    },
    {
        c: 'role',
        q: 'Where would you draw the module boundary?',
        r: '…transport, normalization and view are separate packages, so the socket can be swapped without touching UI…',
        e: 'Layered transport / store / view boundaries',
        s: 83,
        t: '14:47',
    },
];
/* ---------------------------------------------------------------- signals */
export const SIGNAL_BRANCHES = [
    { id: 'voice', label: 'Voice', leaves: ['Transcript', 'Follow-ups', 'Responses'] },
    { id: 'code', label: 'Code', leaves: ['Code', 'AI review', 'Explanation'] },
    { id: 'screen', label: 'Screen', leaves: ['Recording', 'Activity'] },
    { id: 'proctoring', label: 'Proctoring', leaves: ['Face', 'Gaze', 'Head movement', 'Attention'] },
];
/* --------------------------------------------------------------- workflow */
export const JD_LINES = [
    'Own the real-time trading UI used by 40,000 traders.',
    '5+ years shipping React and TypeScript in production.',
    'Deep on rendering performance, state architecture and testing.',
];
export const PLAN = [
    { n: '1', t: 'Frontend architecture', w: 25, q: 3 },
    { n: '2', t: 'React performance', w: 35, q: 3 },
    { n: '3', t: 'Testing & quality', w: 20, q: 2 },
    { n: '4', t: 'Collaboration', w: 20, q: 2, coding: false },
    { n: '5', t: 'Coding · virtualized list', w: 0, q: 1, coding: true },
];
export const REPORT_TABS = [
    'Executive Summary',
    'Evaluation Framework',
    'Objectives',
    'Transcript & Evidence',
    'Coding',
    'Behaviour',
    'Proctoring',
    'Recommendation',
];
/* -------------------------------------------------------------- candidate */
export const CANDIDATE_STEPS = [
    { k: 'invite', label: 'Invite', note: 'One link by email' },
    { k: 'device', label: 'Device check', note: 'Camera, mic, network' },
    { k: 'rules', label: 'Guidelines', note: 'What to expect' },
    { k: 'share', label: 'Screen share', note: 'Entire screen' },
    { k: 'interview', label: 'AI interview', note: 'Voice conversation' },
    { k: 'coding', label: 'Coding, if assigned', note: '11 languages' },
    { k: 'done', label: 'Completed', note: 'Submitted' },
];
export const LANGUAGES = [
    'JavaScript',
    'TypeScript',
    'Python',
    'Java',
    'C++',
    'C',
    'C#',
    'Go',
    'Ruby',
    'PHP',
    'SQL',
];
/* -------------------------------------------------------------------- faq */
export const FAQ = [
    {
        q: 'What is AI interview software?',
        a: 'AI interview software runs a structured interview for you and prepares an assessment your hiring team reviews. With Vriti, you set the role’s criteria, candidates complete a voice interview — and a coding round, if assigned — and you get a scored report.',
    },
    {
        q: 'How does the AI interviewer work?',
        a: 'It asks one question at a time from the objective it is covering, transcribes the candidate’s answer live, checks it against what a complete answer contains, and asks a follow-up when something is missing.',
    },
    {
        q: 'Can Vriti support technical and coding interviews?',
        a: 'Yes. Coding rounds run in an in-browser editor in 11 languages: JavaScript, TypeScript, Python, Java, C++, C, C#, Go, Ruby, PHP and SQL. The submission is evaluated by AI and shown in the report’s Coding tab.',
    },
    {
        q: 'Do candidates need an account or download?',
        a: 'No. Candidates join in the browser from the link in their invitation. They need a camera, a microphone and a stable connection, and they share their entire screen for the session.',
    },
    {
        q: 'How does interview integrity review work?',
        a: 'Six signals are tracked during the session — multiple faces, leaving the frame, tab switches, camera stops, eye movements and head movements — and weighted into an integrity score out of 100. Each session is labelled no integrity concerns, review recommended or integrity concern. A signal is a reason to review, not proof of misconduct.',
    },
    {
        q: 'What is included in the report?',
        a: 'Eight tabs: Executive Summary, Evaluation Framework, Objectives, Transcript & Evidence, Coding, Behaviour, Proctoring and Recommendation. Each report exports as a PDF — summary, detailed or transcript.',
    },
    {
        q: 'Does AI make the hiring decision?',
        a: 'No. The AI prepares the assessment; the hiring team reviews the available evidence and makes the decision.',
    },
    {
        q: 'How are AI interview answers scored?',
        a: 'Every answer is scored against a weighted rubric you set: objectives, sub-objectives and criteria with minimum scores. Each score cites the part of the transcript it came from.',
    },
    {
        q: 'How long is an AI screening interview?',
        a: 'You set the plan. The sample role on this page is a 45-minute interview with architecture, performance, testing, collaboration and coding sections.',
    },
];
/* ------------------------------------------------------------------ final */
